import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Modal,
  Segmented,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  StopOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useApproveConciergePackMutation,
  useGetConciergePackQuery,
  useGetConciergePacksQuery,
  useKillConciergePackMutation,
  useRetryConciergePackMutation,
  useRunConciergeTickMutation,
  useSetConciergeIdentityVerificationMutation,
  useUpdateConciergeDraftMutation,
  type IConciergePackDraft,
  type IConciergePackListRow,
} from '../../services/nestjs-base-api';
import {
  CrmRichTextEditor,
  EmailBodyHtml,
} from '../components/crm/CrmRichTextEditor';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text, Paragraph } = Typography;

const STATUS_COLOURS: Record<string, string> = {
  QUEUED_RESEARCH: 'default',
  RESEARCHING: 'processing',
  GENERATING: 'processing',
  DRAFT: 'gold',
  APPROVED: 'blue',
  SENT: 'green',
  KILLED: 'default',
  FAILED: 'red',
  SKIPPED: 'default',
};

const VERIFICATION_COLOURS: Record<string, string> = {
  verified: 'green',
  unverified: 'orange',
  rejected: 'red',
};

/**
 * Editable set = exactly what reaches the brand. That is the whole answer to
 * "what can I change here?": if it is an input, it goes out and you own it.
 * Internal notes are read-only on the Context tab instead of sitting in
 * textareas that imply editing them changes something.
 */
const EDITABLE_FIELDS: Array<{
  key: keyof IConciergePackDraft;
  labelId: string;
  rows: number;
  hint?: string;
  // Rich text, edited with the same Quill editor as a CRM email. Only the
  // covering email qualifies: the customer copy is plain text on purpose,
  // because the brand pastes it into their own email platform.
  html?: boolean;
}> = [
  {
    key: 'offerDescription',
    labelId: 'concierge.field.offerDescription',
    rows: 2,
    hint: 'concierge.field.offerDescriptionHint',
  },
  {
    key: 'customerEmailSubject',
    labelId: 'concierge.field.customerEmailSubject',
    rows: 1,
  },
  {
    key: 'customerEmailBody',
    labelId: 'concierge.field.customerEmailBody',
    rows: 12,
    hint: 'concierge.field.customerEmailBodyHint',
  },
  {
    key: 'coveringEmailSubject',
    labelId: 'concierge.field.coveringEmailSubject',
    rows: 1,
  },
  {
    key: 'coveringEmailBodyHtml',
    labelId: 'concierge.field.coveringEmailBody',
    rows: 16,
    hint: 'concierge.field.coveringEmailBodyHint',
    html: true,
  },
];

// Generated for the reviewer to read, never sent. Displayed, not editable.
const READ_ONLY_FIELDS: Array<{
  key: keyof IConciergePackDraft;
  labelId: string;
}> = [
  { key: 'incentiveRationale', labelId: 'concierge.field.incentiveRationale' },
  { key: 'researchSummary', labelId: 'concierge.field.researchSummary' },
  { key: 'voiceNotes', labelId: 'concierge.field.voiceNotes' },
];

interface AdminConciergePageProps {
  // Rendered inside the CRM tab set: the page chrome (padding, heading) comes
  // from the host, so it is dropped here to avoid a doubled title.
  embedded?: boolean;
}

export const AdminConciergePage: React.FC<AdminConciergePageProps> = ({
  embedded = false,
}) => {
  const intl = useIntl();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';

  const [statusFilter, setStatusFilter] = useState<string>('DRAFT');
  const [openPackId, setOpenPackId] = useState<number | null>(null);
  const [edits, setEdits] = useState<Partial<IConciergePackDraft>>({});
  // Two-stage send, matching the CRM compose flow: review the assembled email
  // with tokens already substituted, then confirm.
  const [reviewOpen, setReviewOpen] = useState(false);

  const packsQuery = useGetConciergePacksQuery(
    { jwtToken, status: statusFilter === 'ALL' ? undefined : statusFilter },
    { skip: !jwtToken },
  );
  const detailQuery = useGetConciergePackQuery(
    { jwtToken, packId: openPackId as number },
    { skip: !jwtToken || openPackId === null },
  );

  const [updateDraft, updateDraftState] = useUpdateConciergeDraftMutation();
  const [approvePack, approveState] = useApproveConciergePackMutation();
  const [killPack] = useKillConciergePackMutation();
  const [retryPack] = useRetryConciergePackMutation();
  const [runTick, runTickState] = useRunConciergeTickMutation();
  const [setVerification] = useSetConciergeIdentityVerificationMutation();

  const detail = detailQuery.data;
  const draft = detail?.draft ?? null;
  const lint = detail?.pack.lintJson ?? [];

  const merged: IConciergePackDraft | null = useMemo(
    () => (draft ? { ...draft, ...edits } : null),
    [draft, edits],
  );
  const hasEdits = Object.keys(edits).length > 0;

  /**
   * Records an edit only when the value genuinely differs from where the field
   * started, and clears it again if the user reverts by hand.
   *
   * The baseline is captured from the first value each field emits rather than
   * read off the draft, because ReactQuill rewrites its input on mount:
   * `<p>Thanks,<br>Steve</p>` becomes `<p>Thanks,</p><p>Steve</p>`. Comparing
   * against the stored string therefore marked every pack dirty the instant it
   * was opened, and would have saved that rewrite back as if it were an edit.
   * Keyed by pack so switching packs cannot inherit a stale baseline.
   */
  const baselines = useRef<{ packId: number | null; values: Record<string, string> }>({
    packId: null,
    values: {},
  });

  const applyEdit = (key: keyof IConciergePackDraft, value: string) => {
    if (baselines.current.packId !== openPackId) {
      baselines.current = { packId: openPackId, values: {} };
    }
    const seen = baselines.current.values;
    if (!(key in seen)) {
      // First emission for this field is where it started, not a change.
      seen[key] = value;
      return;
    }
    setEdits((prev) => {
      if (value === seen[key]) {
        if (!(key in prev)) return prev;
        const { [key]: _dropped, ...rest } = prev;
        return rest;
      }
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  };

  const closeDrawer = () => {
    setOpenPackId(null);
    setEdits({});
    setReviewOpen(false);
    baselines.current = { packId: null, values: {} };
  };

  const handleSave = async () => {
    if (openPackId === null || !hasEdits) return;
    try {
      const result = await updateDraft({
        jwtToken,
        packId: openPackId,
        patch: edits,
      }).unwrap();
      setEdits({});
      if (result.lint.length > 0) {
        message.warning(
          intl.formatMessage(
            { id: 'concierge.saved.withViolations' },
            { count: result.lint.length },
          ),
        );
      } else {
        message.success(intl.formatMessage({ id: 'concierge.saved' }));
      }
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to save');
    }
  };

  // Called from the review modal's confirm, so the thing sent is the thing
  // just read on screen.
  const handleConfirmSend = async () => {
    if (openPackId === null) return;
    try {
      const result = await approvePack({
        jwtToken,
        packId: openPackId,
        overrideLint: lint.length > 0,
      }).unwrap();
      message.success(
        intl.formatMessage({ id: 'concierge.approve.sent' }, { to: result.to }),
      );
      closeDrawer();
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to send');
    }
  };

  const handleKill = () => {
    if (openPackId === null) return;
    let reason = '';
    Modal.confirm({
      title: intl.formatMessage({ id: 'concierge.kill.confirmTitle' }),
      icon: <StopOutlined />,
      content: (
        <Input.TextArea
          rows={3}
          placeholder={intl.formatMessage({ id: 'concierge.kill.reasonPlaceholder' })}
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: intl.formatMessage({ id: 'concierge.kill.okText' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await killPack({ jwtToken, packId: openPackId, reason }).unwrap();
          message.success(intl.formatMessage({ id: 'concierge.kill.done' }));
          closeDrawer();
        } catch (error: any) {
          message.error(error?.data?.message || 'Failed to kill pack');
        }
      },
    });
  };

  const handleVerification = async (
    status: 'unverified' | 'verified' | 'rejected',
  ) => {
    if (!detail) return;
    try {
      await setVerification({
        jwtToken,
        organisationId: detail.pack.organisationId,
        status,
      }).unwrap();
      message.success(intl.formatMessage({ id: 'concierge.verification.updated' }));
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to update');
    }
  };

  const columns: ColumnsType<IConciergePackListRow> = [
    {
      title: <FormattedMessage id="concierge.column.brand" />,
      key: 'brand',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.brandName}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.organisationName}
          </Text>
        </Space>
      ),
    },
    {
      title: <FormattedMessage id="concierge.column.status" />,
      dataIndex: ['pack', 'status'],
      key: 'status',
      render: (status: string) => (
        <Tag color={STATUS_COLOURS[status] ?? 'default'}>{status}</Tag>
      ),
    },
    {
      title: <FormattedMessage id="concierge.column.identity" />,
      dataIndex: 'identityVerificationStatus',
      key: 'identity',
      render: (status: string) => (
        <Tag color={VERIFICATION_COLOURS[status] ?? 'default'}>
          <FormattedMessage id={`concierge.verification.${status}`} />
        </Tag>
      ),
    },
    {
      title: <FormattedMessage id="concierge.column.checks" />,
      dataIndex: 'lintCount',
      key: 'lint',
      render: (count: number) =>
        count > 0 ? (
          <Tag color="red" icon={<WarningOutlined />}>
            <FormattedMessage id="concierge.lint.count" values={{ count }} />
          </Tag>
        ) : (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            <FormattedMessage id="concierge.lint.clear" />
          </Tag>
        ),
    },
    {
      title: <FormattedMessage id="concierge.column.trigger" />,
      dataIndex: ['pack', 'triggerSource'],
      key: 'trigger',
      render: (source: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {source}
        </Text>
      ),
    },
    {
      title: <FormattedMessage id="concierge.column.updated" />,
      dataIndex: ['pack', 'updatedAt'],
      key: 'updated',
      render: (value: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(value).format('D MMM YYYY HH:mm')}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined style={{ marginRight: 4 }} />}
            onClick={() => {
              setEdits({});
              setOpenPackId(row.pack.id);
            }}
          >
            <FormattedMessage id="concierge.action.review" />
          </Button>
          {(row.pack.status === 'FAILED' || row.pack.status === 'KILLED') && (
            <Button
              size="small"
              onClick={async () => {
                await retryPack({ jwtToken, packId: row.pack.id });
                message.success(intl.formatMessage({ id: 'concierge.action.requeued' }));
              }}
            >
              <FormattedMessage id="concierge.action.retry" />
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      <Space
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
        align="start"
      >
        <div>
          {!embedded && (
            <Title level={3} style={{ margin: 0 }}>
              <ExperimentOutlined style={{ marginRight: 8 }} />
              <FormattedMessage id="concierge.title" />
            </Title>
          )}
          <Text type="secondary">
            <FormattedMessage id="concierge.subtitle" />
          </Text>
        </div>
        <Space>
          <Button
            icon={<ThunderboltOutlined style={{ marginRight: 4 }} />}
            loading={runTickState.isLoading}
            onClick={async () => {
              await runTick({ jwtToken });
              message.success(intl.formatMessage({ id: 'concierge.action.tickRan' }));
            }}
          >
            <FormattedMessage id="concierge.action.runTick" />
          </Button>
          <Button
            icon={<ReloadOutlined style={{ marginRight: 4 }} />}
            onClick={() => packsQuery.refetch()}
          >
            <FormattedMessage id="concierge.action.refresh" />
          </Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={<FormattedMessage id="concierge.gate.title" />}
        description={<FormattedMessage id="concierge.gate.description" />}
      />

      <Card>
        <Segmented
          style={{ marginBottom: 16 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as string)}
          options={[
            { label: intl.formatMessage({ id: 'concierge.filter.draft' }), value: 'DRAFT' },
            { label: intl.formatMessage({ id: 'concierge.filter.sent' }), value: 'SENT' },
            { label: intl.formatMessage({ id: 'concierge.filter.failed' }), value: 'FAILED' },
            { label: intl.formatMessage({ id: 'concierge.filter.all' }), value: 'ALL' },
          ]}
        />
        <Table
          rowKey={(row) => row.pack.id}
          loading={packsQuery.isLoading || packsQuery.isFetching}
          dataSource={packsQuery.data ?? []}
          columns={columns}
          pagination={{ pageSize: 20 }}
          locale={{
            emptyText: (
              <Empty description={intl.formatMessage({ id: 'concierge.empty' })} />
            ),
          }}
        />
      </Card>

      <Drawer
        open={openPackId !== null}
        onClose={closeDrawer}
        width={980}
        // App.css sets a global `.ant-drawer-body { text-align: center }`,
        // which centres every drawer in the app. Overridden here rather than
        // globally: this pack is prose and email copy that must read left
        // aligned, and changing the shared rule would restyle every other
        // drawer at the same time.
        styles={{ body: { textAlign: 'left' } }}
        title={
          detail
            ? `${detail.brandName} — ${detail.organisationName}`
            : intl.formatMessage({ id: 'concierge.title' })
        }
        extra={
          <Space>
            <Button onClick={handleKill} danger icon={<StopOutlined style={{ marginRight: 4 }} />}>
              <FormattedMessage id="concierge.action.kill" />
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasEdits}
              loading={updateDraftState.isLoading}
            >
              <FormattedMessage id="concierge.action.save" />
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined style={{ marginRight: 4 }} />}
              disabled={detail?.pack.status !== 'DRAFT'}
              onClick={() => setReviewOpen(true)}
            >
              <FormattedMessage id="concierge.action.review" />
            </Button>
          </Space>
        }
      >
        {detailQuery.isLoading && <Spin />}

        {detail && merged && (
          <Tabs
            defaultActiveKey="copy"
            items={[
              {
                key: 'copy',
                label: (
                  <span>
                    <EditOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="concierge.tab.copy" />
                  </span>
                ),
                children: (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {hasEdits && (
                      <Alert
                        type="warning"
                        showIcon
                        message={<FormattedMessage id="concierge.unsaved" />}
                      />
                    )}

                    {lint.length > 0 && (
                      <Alert
                        type="error"
                        showIcon
                        message={
                          <FormattedMessage
                            id="concierge.lint.blockTitle"
                            values={{ count: lint.length }}
                          />
                        }
                        description={
                          <ul style={{ margin: '8px 0 0 0', paddingLeft: 18 }}>
                            {lint.map((violation, index) => (
                              <li key={index}>
                                <Text strong>{violation.field}</Text> <Tag>{violation.rule}</Tag>
                                <div>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {violation.detail}
                                  </Text>
                                </div>
                                {violation.excerpt && (
                                  <div>
                                    <Text code style={{ fontSize: 12 }}>
                                      {violation.excerpt}
                                    </Text>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        }
                      />
                    )}

                    {detail.pack.status !== 'DRAFT' && (
                      <Alert
                        type="info"
                        showIcon
                        message={<FormattedMessage id="concierge.frozen" />}
                      />
                    )}

                    <Text type="secondary">
                      <FormattedMessage id="concierge.tab.copyHint" />
                    </Text>

                    {EDITABLE_FIELDS.map((field) => (
                      <div key={String(field.key)}>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>
                          <FormattedMessage id={field.labelId} />
                        </Text>
                        {field.hint && (
                          <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <FormattedMessage id={field.hint} />
                            </Text>
                          </div>
                        )}
                        {field.html ? (
                          detail.pack.status === 'DRAFT' ? (
                            <CrmRichTextEditor
                              minHeight={320}
                              value={String(merged[field.key] ?? '')}
                              onChange={(value) => applyEdit(field.key, value)}
                            />
                          ) : (
                            // Frozen packs render rather than edit, so a sent
                            // email is never shown as if it were still editable.
                            <EmailBodyHtml
                              body={String(merged[field.key] ?? '')}
                              style={{
                                border: '1px solid #f0f0f0',
                                borderRadius: 6,
                                padding: '12px 16px',
                                background: '#fafafa',
                              }}
                            />
                          )
                        ) : (
                          <Input.TextArea
                            rows={field.rows}
                            value={String(merged[field.key] ?? '')}
                            disabled={detail.pack.status !== 'DRAFT'}
                            onChange={(e) => applyEdit(field.key, e.target.value)}
                          />
                        )}
                      </div>
                    ))}
                  </Space>
                ),
              },
              {
                key: 'context',
                label: (
                  <span>
                    <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="concierge.tab.context" />
                  </span>
                ),
                children: (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text type="secondary">
                      <FormattedMessage id="concierge.tab.contextHint" />
                    </Text>

                    <Card
                      size="small"
                      title={<FormattedMessage id="concierge.signals.title" />}
                      extra={
                        <Space>
                          {(['verified', 'unverified', 'rejected'] as const).map((status) => (
                            <Button
                              key={status}
                              size="small"
                              type={
                                detail.signals.identityVerificationStatus === status
                                  ? 'primary'
                                  : 'default'
                              }
                              danger={status === 'rejected'}
                              onClick={() => handleVerification(status)}
                            >
                              <FormattedMessage id={`concierge.verification.${status}`} />
                            </Button>
                          ))}
                        </Space>
                      }
                    >
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={<FormattedMessage id="concierge.signals.note" />}
                      />
                      <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item
                          label={<FormattedMessage id="concierge.signals.contact" />}
                        >
                          {detail.signals.contactEmail ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item
                          label={<FormattedMessage id="concierge.signals.domainMatch" />}
                        >
                          {detail.signals.domainsMatch ? (
                            <Tag color="green">
                              <FormattedMessage id="concierge.signals.domainMatchYes" />
                            </Tag>
                          ) : (
                            <Tag color="orange">
                              <FormattedMessage id="concierge.signals.domainMatchNo" />
                            </Tag>
                          )}
                          {detail.signals.isFreeMailDomain && (
                            <Tag color="orange">
                              <FormattedMessage id="concierge.signals.freeMail" />
                            </Tag>
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item
                          label={<FormattedMessage id="concierge.signals.brandDomain" />}
                        >
                          {detail.signals.brandDomain ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item
                          label={<FormattedMessage id="concierge.signals.lastSeen" />}
                        >
                          {detail.signals.postHogAvailable
                            ? detail.signals.lastSeenAt
                              ? dayjs(detail.signals.lastSeenAt).format('D MMM YYYY')
                              : intl.formatMessage({ id: 'concierge.signals.never' })
                            : intl.formatMessage({ id: 'concierge.signals.noPostHog' })}
                        </Descriptions.Item>
                        <Descriptions.Item
                          label={<FormattedMessage id="concierge.signals.activity" />}
                          span={2}
                        >
                          {detail.signals.postHogAvailable ? (
                            <FormattedMessage
                              id="concierge.signals.activityValue"
                              values={{
                                events: detail.signals.events30d ?? 0,
                                days: detail.signals.activeDays30d ?? 0,
                              }}
                            />
                          ) : (
                            <Text type="secondary">
                              <FormattedMessage id="concierge.signals.noPostHog" />
                            </Text>
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title={<FormattedMessage id="concierge.flags.title" />}>
                      <Space wrap>
                        {merged.flags.regulated && (
                          <Tag color="red">
                            <FormattedMessage id="concierge.flag.regulated" />
                          </Tag>
                        )}
                        {merged.flags.gifting && (
                          <Tag color="purple">
                            <FormattedMessage id="concierge.flag.gifting" />
                          </Tag>
                        )}
                        {merged.flags.lowRepurchase && (
                          <Tag color="orange">
                            <FormattedMessage id="concierge.flag.lowRepurchase" />
                          </Tag>
                        )}
                        {merged.flags.noBackCatalogue && (
                          <Tag color="blue">
                            <FormattedMessage id="concierge.flag.noBackCatalogue" />
                          </Tag>
                        )}
                        {merged.flags.multiMarket && (
                          <Tag color="geekblue">
                            <FormattedMessage id="concierge.flag.multiMarket" />
                          </Tag>
                        )}
                        {merged.flags.smallCustomerBase && (
                          <Tag color="orange">
                            <FormattedMessage id="concierge.flag.smallCustomerBase" />
                          </Tag>
                        )}
                        {!merged.flags.regulated &&
                          !merged.flags.gifting &&
                          !merged.flags.lowRepurchase &&
                          !merged.flags.noBackCatalogue &&
                          !merged.flags.multiMarket &&
                          !merged.flags.smallCustomerBase && (
                            <Text type="secondary">
                              <FormattedMessage id="concierge.flag.none" />
                            </Text>
                          )}
                      </Space>
                      {merged.flags.notes && (
                        <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                          <Text type="secondary">{merged.flags.notes}</Text>
                        </Paragraph>
                      )}
                    </Card>

                    <Card size="small" title={<FormattedMessage id="concierge.links.title" />}>
                      {detail.surveyLinks.map((link) => (
                        <div key={link.surveyHash} style={{ marginBottom: 8 }}>
                          <Space>
                            <Tag color={link.kind === 'past' ? 'blue' : 'green'}>
                              <FormattedMessage id={`concierge.audience.${link.kind}`} />
                            </Tag>
                            {!link.recommended && (
                              <Tag>
                                <FormattedMessage id="concierge.audience.notRecommended" />
                              </Tag>
                            )}
                            <Text>{link.surveyName}</Text>
                          </Space>
                          <div>
                            <Text copyable code style={{ fontSize: 12 }}>
                              {link.bareUrl}
                            </Text>
                          </div>
                          <div>
                            <Tooltip
                              title={intl.formatMessage({ id: 'concierge.links.testTooltip' })}
                            >
                              <Text copyable code type="secondary" style={{ fontSize: 12 }}>
                                {link.testUrl}
                              </Text>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                      {merged.thankYouButtonUrl && (
                        <Paragraph style={{ marginBottom: 0, marginTop: 8 }}>
                          <Text type="secondary">
                            <FormattedMessage id="concierge.links.thankYou" />
                          </Text>
                          <br />
                          <Text code style={{ fontSize: 12 }}>
                            {merged.thankYouButtonUrl}
                          </Text>
                        </Paragraph>
                      )}
                    </Card>

                    {READ_ONLY_FIELDS.map((field) => (
                      <Card
                        key={String(field.key)}
                        size="small"
                        title={<FormattedMessage id={field.labelId} />}
                      >
                        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                          {String(merged[field.key] ?? '—')}
                        </Paragraph>
                      </Card>
                    ))}

                    {detail.pack.lastError && (
                      <Alert
                        type="error"
                        showIcon
                        message={<FormattedMessage id="concierge.lastError" />}
                        description={detail.pack.lastError}
                      />
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}

        <Modal
          open={reviewOpen}
          width={760}
          title={<FormattedMessage id="concierge.review.title" />}
          onCancel={() => setReviewOpen(false)}
          cancelText={<FormattedMessage id="concierge.review.back" />}
          okText={<FormattedMessage id="concierge.review.confirm" />}
          okButtonProps={{
            danger: true,
            loading: approveState.isLoading,
            disabled: hasEdits || !detail?.emailPreview,
          }}
          onOk={handleConfirmSend}
        >
          {hasEdits && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={<FormattedMessage id="concierge.review.unsavedBlocks" />}
            />
          )}

          {detail?.emailPreviewError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={<FormattedMessage id="concierge.preview.error" />}
              description={detail.emailPreviewError}
            />
          )}

          {lint.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={intl.formatMessage(
                { id: 'concierge.approve.overrideWarning' },
                { count: lint.length },
              )}
            />
          )}

          {detail?.signals.identityVerificationStatus !== 'verified' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={intl.formatMessage({
                id: 'concierge.approve.unverifiedWarning',
              })}
            />
          )}

          {detail?.emailPreview && (
            <>
              <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
                <Descriptions.Item label={<FormattedMessage id="concierge.review.from" />}>
                  {detail.sendPreview?.fromLabel ? (
                    <>
                      "{detail.sendPreview?.fromLabel}"
                      {detail.sendPreview?.fromAddress && (
                        <Text type="secondary" style={{ marginLeft: 6 }}>
                          &lt;{detail.sendPreview?.fromAddress}&gt;
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text type="secondary">
                      <FormattedMessage id="concierge.review.fromUnknown" />
                    </Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="concierge.review.to" />}>
                  {detail.sendPreview?.to ? (
                    <Text strong>
                      {detail.sendPreview?.to}
                      {detail.sendPreview?.contactName
                        ? ` (${detail.sendPreview?.contactName})`
                        : ''}
                    </Text>
                  ) : (
                    <Text type="danger">
                      <FormattedMessage id="concierge.review.noContact" />
                    </Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="concierge.review.subject" />}>
                  <Text strong>{detail.emailPreview.subject}</Text>
                </Descriptions.Item>
              </Descriptions>

              <Text type="secondary" style={{ fontSize: 12 }}>
                <FormattedMessage id="concierge.review.substituted" />
              </Text>
              <EmailBodyHtml
                body={detail.emailPreview.bodyHtml}
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '12px 16px',
                  background: '#fafafa',
                  maxHeight: 420,
                  overflowY: 'auto',
                  marginTop: 8,
                }}
              />
            </>
          )}
        </Modal>
      </Drawer>
    </div>
  );
};

export default AdminConciergePage;
